/**
 * Offline Dashboard Service
 *
 * Manages offline data storage using IndexedDB for dashboard content.
 * Provides read-only access to cached dashboard views and recent content.
 * Handles automatic background sync when connection is restored.
 *
 * @author AI Assistant
 * @version 2.0.0
 */
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
// =============================================================================
// TYPES & INTERFACES
// =============================================================================
interface DashboardDB extends DBSchema {
    dashboardData: {
        key: string;
        value: {
            id: string;
            data: any;
            timestamp: number;
            expiresAt: number;
        };
        indexes: {
            'by-expiry': number;
        };
    };
    events: {
        key: number;
        value: {
            id: number;
            title: string;
            description: string;
            start_date: string;
            end_date: string;
            location: string;
            data: any;
            timestamp: number;
        };
        indexes: {
            'by-date': string;
        };
    };
    notifications: {
        key: number;
        value: {
            id: number;
            title: string;
            message: string;
            type: string;
            priority: string;
            read: boolean;
            timestamp: number;
            data: any;
        };
        indexes: {
            'by-timestamp': number;
            'by-read': number;
        };
    };
    userProfile: {
        key: string;
        value: {
            userId: number;
            data: any;
            timestamp: number;
        };
    };
    syncQueue: {
        key: string;
        value: {
            id: string;
            url: string;
            method: string;
            headers: Record<string, string>;
            body: any;
            timestamp: number;
            retryCount: number;
            /**
             * The id of the user who originated this queued mutation. Stamped
             * at queue time so that on reconnect we can verify the
             * re-authenticated user matches before flushing — preventing one
             * user's offline queue from being POSTed under another user on
             * shared devices.
             */
            actorUserId?: string;
        };
        indexes: {
            'by-timestamp': number;
        };
    };
}
interface CacheConfig {
    dashboardTTL: number; // Time to live for dashboard data
    eventsTTL: number; // Time to live for events
    notificationsTTL: number; // Time to live for notifications
    profileTTL: number; // Time to live for user profile
    maxRetries: number; // Max retry attempts for sync queue
}
// =============================================================================
// OFFLINE DASHBOARD SERVICE
// =============================================================================
class OfflineDashboardService {
    private static instance: OfflineDashboardService;
    private db: IDBPDatabase<DashboardDB> | null = null;
    private isInitialized = false;
    private syncInProgress = false;
    private config: CacheConfig = {
        dashboardTTL: 24 * 60 * 60 * 1000, // 24 hours
        eventsTTL: 12 * 60 * 60 * 1000, // 12 hours
        notificationsTTL: 6 * 60 * 60 * 1000, // 6 hours
        profileTTL: 24 * 60 * 60 * 1000, // 24 hours
        maxRetries: 3
    };
    private constructor() {
        // Private constructor for singleton
    }
    /**
     * Get singleton instance
     */
    public static getInstance(): OfflineDashboardService {
        if (!OfflineDashboardService.instance) {
            OfflineDashboardService.instance = new OfflineDashboardService();
        }
        return OfflineDashboardService.instance;
    }
    /**
     * Initialize the IndexedDB database
     */
    public async initialize(): Promise<void> {
        if (this.isInitialized && this.db) {
            return;
        }
        try {
            this.db = await openDB<DashboardDB>('apt-dashboard-offline', 1, {
                upgrade(db) {
                    // Dashboard data store
                    if (!db.objectStoreNames.contains('dashboardData')) {
                        const dashboardStore = db.createObjectStore('dashboardData', { keyPath: 'id' });
                        dashboardStore.createIndex('by-expiry', 'expiresAt');
                    }
                    // Events store
                    if (!db.objectStoreNames.contains('events')) {
                        const eventsStore = db.createObjectStore('events', { keyPath: 'id' });
                        eventsStore.createIndex('by-date', 'start_date');
                    }
                    // Notifications store
                    if (!db.objectStoreNames.contains('notifications')) {
                        const notificationsStore = db.createObjectStore('notifications', { keyPath: 'id' });
                        notificationsStore.createIndex('by-timestamp', 'timestamp');
                        notificationsStore.createIndex('by-read', 'read');
                    }
                    // User profile store
                    if (!db.objectStoreNames.contains('userProfile')) {
                        db.createObjectStore('userProfile', { keyPath: 'userId' });
                    }
                    // Sync queue store
                    if (!db.objectStoreNames.contains('syncQueue')) {
                        const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
                        syncStore.createIndex('by-timestamp', 'timestamp');
                    }
                },
            });
            this.isInitialized = true;
            logger.debug('✅ Offline Dashboard Service initialized');
            // Clean up expired data
            this.cleanupExpiredData();
            // Set up periodic cleanup
            setInterval(() => this.cleanupExpiredData(), 60 * 60 * 1000); // Every hour
        }
        catch (error) {
            logger.error('❌ Failed to initialize Offline Dashboard Service:', error, {});
            throw error;
        }
    }
    /**
     * Check if service is initialized
     */
    public isReady(): boolean {
        return this.isInitialized && this.db !== null;
    }
    // =============================================================================
    // DASHBOARD DATA MANAGEMENT
    // =============================================================================
    /**
     * Cache dashboard data
     */
    public async cacheDashboardData(key: string, data: any, ttl?: number): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        const expiresAt = Date.now() + (ttl || this.config.dashboardTTL);
        await this.db!.put('dashboardData', {
            id: key,
            data,
            timestamp: Date.now(),
            expiresAt
        });
        logger.security(`📦 Cached dashboard data: ${key}`);
    }
    /**
     * Get cached dashboard data
     */
    public async getCachedDashboardData(key: string): Promise<any | null> {
        if (!this.db) {
            await this.initialize();
        }
        const cached = await this.db!.get('dashboardData', key);
        if (!cached) {
            return null;
        }
        // Check if expired
        if (cached.expiresAt < Date.now()) {
            await this.db!.delete('dashboardData', key);
            logger.security(`🗑️ Expired dashboard data removed: ${key}`);
            return null;
        }
        logger.security(`✅ Retrieved cached dashboard data: ${key}`);
        return cached.data;
    }
    /**
     * Clear dashboard data cache
     */
    public async clearDashboardCache(): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        await this.db!.clear('dashboardData');
        logger.debug('🗑️ Dashboard cache cleared');
    }
    // =============================================================================
    // EVENTS MANAGEMENT
    // =============================================================================
    /**
     * Cache events
     */
    public async cacheEvents(events: any[]): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        const tx = this.db!.transaction('events', 'readwrite');
        for (const event of events) {
            await tx.store.put({
                id: event.id,
                title: event.title,
                description: event.description,
                start_date: event.start_date,
                end_date: event.end_date,
                location: event.location,
                data: event,
                timestamp: Date.now()
            });
        }
        await tx.done;
        logger.debug(`📅 Cached ${events.length} events`);
    }
    /**
     * Get cached events
     */
    public async getCachedEvents(): Promise<any[]> {
        if (!this.db) {
            await this.initialize();
        }
        const events = await this.db!.getAll('events');
        // Filter out expired events
        const now = Date.now();
        const validEvents = events.filter(event => event.timestamp + this.config.eventsTTL > now);
        logger.debug(`✅ Retrieved ${validEvents.length} cached events`);
        return validEvents.map(e => e.data);
    }
    /**
     * Get upcoming cached events
     */
    public async getUpcomingCachedEvents(limit: number = 10): Promise<any[]> {
        if (!this.db) {
            await this.initialize();
        }
        const now = new Date().toISOString();
        const events = await this.db!.getAllFromIndex('events', 'by-date');
        const upcomingEvents = events
            .filter(event => event.start_date >= now)
            .sort((a, b) => a.start_date.localeCompare(b.start_date))
            .slice(0, limit);
        logger.debug(`✅ Retrieved ${upcomingEvents.length} upcoming events`);
        return upcomingEvents.map(e => e.data);
    }
    // =============================================================================
    // NOTIFICATIONS MANAGEMENT
    // =============================================================================
    /**
     * Cache notifications
     */
    public async cacheNotifications(notifications: any[]): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        const tx = this.db!.transaction('notifications', 'readwrite');
        for (const notification of notifications) {
            await tx.store.put({
                id: notification.id,
                title: notification.title,
                message: notification.message,
                type: notification.type,
                priority: notification.priority,
                read: notification.read || false,
                timestamp: new Date(notification.created_at || Date.now()).getTime(),
                data: notification
            });
        }
        await tx.done;
        logger.debug(`🔔 Cached ${notifications.length} notifications`);
    }
    /**
     * Get cached notifications
     */
    public async getCachedNotifications(unreadOnly: boolean = false): Promise<any[]> {
        if (!this.db) {
            await this.initialize();
        }
        let notifications;
        if (unreadOnly) {
            notifications = await this.db!.getAllFromIndex('notifications', 'by-read', 0);
        }
        else {
            notifications = await this.db!.getAll('notifications');
        }
        // Filter out expired notifications
        const now = Date.now();
        const validNotifications = notifications.filter(notification => notification.timestamp + this.config.notificationsTTL > now);
        // Sort by timestamp descending
        validNotifications.sort((a, b) => b.timestamp - a.timestamp);
        logger.debug(`✅ Retrieved ${validNotifications.length} cached notifications`);
        return validNotifications.map(n => n.data);
    }
    /**
     * Mark notification as read (offline)
     */
    public async markNotificationAsRead(notificationId: number): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        const notification = await this.db!.get('notifications', notificationId);
        if (notification) {
            notification.read = true;
            notification.data.read = true;
            await this.db!.put('notifications', notification);
            logger.debug(`✅ Marked notification ${notificationId} as read (offline)`);
        }
    }
    // =============================================================================
    // USER PROFILE MANAGEMENT
    // =============================================================================
    /**
     * Cache user profile
     */
    public async cacheUserProfile(userId: number, profileData: any): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        await this.db!.put('userProfile', {
            userId,
            data: profileData,
            timestamp: Date.now()
        });
        logger.debug(`👤 Cached user profile for user ${userId}`);
    }
    /**
     * Get cached user profile
     */
    public async getCachedUserProfile(userId: number): Promise<any | null> {
        if (!this.db) {
            await this.initialize();
        }
        const profile = await this.db!.get('userProfile', userId.toString());
        if (!profile) {
            return null;
        }
        // Check if expired
        if (profile.timestamp + this.config.profileTTL < Date.now()) {
            await this.db!.delete('userProfile', userId.toString());
            logger.debug(`🗑️ Expired profile removed for user ${userId}`);
            return null;
        }
        logger.debug(`✅ Retrieved cached profile for user ${userId}`);
        return profile.data;
    }
    // =============================================================================
    // SYNC QUEUE MANAGEMENT
    // =============================================================================
    /**
     * Add request to sync queue
     */
    public async addToSyncQueue(
        url: string,
        method: string,
        headers: Record<string, string>,
        body: any,
        actorUserId?: string,
    ): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        await this.db!.put('syncQueue', {
            id,
            url,
            method,
            headers,
            body,
            timestamp: Date.now(),
            retryCount: 0,
            actorUserId,
        });
        logger.api(`📤 Added request to sync queue: ${method} ${url}`, "GET");
    }
    /**
     * Get all pending sync requests
     */
    public async getSyncQueue(): Promise<any[]> {
        if (!this.db) {
            await this.initialize();
        }
        const queue = await this.db!.getAll('syncQueue');
        return queue;
    }
    /**
     * Process sync queue
     */
    public async processSyncQueue(currentActorUserId?: string): Promise<{
        success: number;
        failed: number;
        held: number;
    }> {
        if (!this.db || this.syncInProgress) {
            return { success: 0, failed: 0, held: 0 };
        }
        this.syncInProgress = true;
        logger.debug('🔄 Processing sync queue...');
        const queue = await this.getSyncQueue();
        let success = 0;
        let failed = 0;
        let held = 0;
        for (const item of queue) {
            // Actor verification — never POST an offline mutation under a
            // different user than the one who created it. Items without an
            // `actorUserId` (legacy entries, or non-K9 callers that haven't
            // adopted the field) are processed as before to avoid
            // regressions.
            if (
                item.actorUserId !== undefined &&
                currentActorUserId !== undefined &&
                String(item.actorUserId) !== String(currentActorUserId)
            ) {
                held++;
                logger.warn(
                    `🔒 Holding queued ${item.method} ${item.url}: actor mismatch (queued=${item.actorUserId}, current=${currentActorUserId})`,
                );
                continue;
            }
            try {
                const response = await fetch(item.url, {
                    method: item.method,
                    headers: item.headers,
                    body: item.body ? JSON.stringify(item.body) : undefined,
                    credentials: 'include'
                });
                if (response.ok) {
                    await this.db!.delete('syncQueue', item.id);
                    success++;
                    logger.debug(`✅ Synced: ${item.method} ${item.url}`);
                }
                else {
                    // Increment retry count
                    item.retryCount++;
                    if (item.retryCount >= this.config.maxRetries) {
                        // Remove after max retries
                        await this.db!.delete('syncQueue', item.id);
                        failed++;
                        logger.error("Error occurred", `❌ Max retries reached for: ${item.method} ${item.url}`, {});
                    }
                    else {
                        await this.db!.put('syncQueue', item);
                        logger.warn(`⚠️ Retry ${item.retryCount}/${this.config.maxRetries}: ${item.method} ${item.url}`);
                    }
                }
            }
            catch (error) {
                item.retryCount++;
                if (item.retryCount >= this.config.maxRetries) {
                    await this.db!.delete('syncQueue', item.id);
                    failed++;
                    logger.error(`❌ Failed after max retries: ${item.method} ${item.url}`, error, {});
                }
                else {
                    await this.db!.put('syncQueue', item);
                    logger.error(`⚠️ Error on retry ${item.retryCount}/${this.config.maxRetries}:`, error, {});
                }
            }
        }
        this.syncInProgress = false;
        logger.debug(`✅ Sync complete: ${success} successful, ${failed} failed, ${held} held`);
        return { success, failed, held };
    }
    /**
     * Clear sync queue
     */
    public async clearSyncQueue(): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        await this.db!.clear('syncQueue');
        logger.debug('🗑️ Sync queue cleared');
    }
    // =============================================================================
    // CLEANUP & MAINTENANCE
    // =============================================================================
    /**
     * Clean up expired data
     */
    private async cleanupExpiredData(): Promise<void> {
        if (!this.db) {
            return;
        }
        try {
            const now = Date.now();
            // Clean up expired dashboard data
            const dashboardData = await this.db.getAll('dashboardData');
            const expiredDashboard = dashboardData.filter(item => item.expiresAt < now);
            for (const item of expiredDashboard) {
                await this.db.delete('dashboardData', item.id);
            }
            // Clean up old events
            const events = await this.db.getAll('events');
            const expiredEvents = events.filter(event => event.timestamp + this.config.eventsTTL < now);
            for (const event of expiredEvents) {
                await this.db.delete('events', event.id);
            }
            // Clean up old notifications
            const notifications = await this.db.getAll('notifications');
            const expiredNotifications = notifications.filter(notification => notification.timestamp + this.config.notificationsTTL < now);
            for (const notification of expiredNotifications) {
                await this.db.delete('notifications', notification.id);
            }
            const totalCleaned = expiredDashboard.length + expiredEvents.length + expiredNotifications.length;
            if (totalCleaned > 0) {
                logger.debug(`🧹 Cleaned up ${totalCleaned} expired items`);
            }
        }
        catch (error) {
            logger.error('❌ Error cleaning up expired data:', error, {});
        }
    }
    /**
     * Get storage statistics
     */
    public async getStorageStats(): Promise<{
        dashboardItems: number;
        events: number;
        notifications: number;
        syncQueueSize: number;
    }> {
        if (!this.db) {
            await this.initialize();
        }
        const [dashboardItems, events, notifications, syncQueue] = await Promise.all([
            this.db!.count('dashboardData'),
            this.db!.count('events'),
            this.db!.count('notifications'),
            this.db!.count('syncQueue')
        ]);
        return {
            dashboardItems,
            events,
            notifications,
            syncQueueSize: syncQueue
        };
    }
    /**
     * Clear all offline data
     */
    public async clearAllData(): Promise<void> {
        if (!this.db) {
            await this.initialize();
        }
        await Promise.all([
            this.db!.clear('dashboardData'),
            this.db!.clear('events'),
            this.db!.clear('notifications'),
            this.db!.clear('userProfile'),
            this.db!.clear('syncQueue')
        ]);
        logger.debug('🗑️ All offline data cleared');
    }
}
// Export singleton instance
export const offlineDashboardService = OfflineDashboardService.getInstance();
export default offlineDashboardService;
